import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";

export default function CountyModal({ open, onOpenChange, county, onSaved }) {
  const blank = { name: "", contact_name: "", contact_email: "", address: "", annual_contract_amount: "", contract_start: "", contract_end: "", seat_limit: 0 };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (county) {
      setForm({
        name: county.name || "",
        contact_name: county.contact_name || "",
        contact_email: county.contact_email || "",
        address: county.address || "",
        annual_contract_amount: county.annual_contract_amount ?? "",
        contract_start: county.contract_start || "",
        contract_end: county.contract_end || "",
        seat_limit: county.seat_limit ?? 0,
      });
    } else {
      setForm(blank);
    }
  }, [county, open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, annual_contract_amount: parseFloat(form.annual_contract_amount) || 0, seat_limit: parseInt(form.seat_limit, 10) || 0 };
    try {
      if (county) await api.put(`/counties/${county.id}`, payload);
      else await api.post("/counties", payload);
      toast.success(county ? "County updated" : "County added");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="county-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Building2 className="h-5 w-5 text-[#0d9488]" /> {county ? "Edit County" : "Add County / Organization"}
          </DialogTitle>
          <DialogDescription>Contracted client billed for platform use.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>County / Organization name</Label>
            <Input data-testid="county-name-input" value={form.name} onChange={set("name")} required placeholder="Maricopa County DCS" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Contact name</Label>
              <Input data-testid="county-contact-input" value={form.contact_name} onChange={set("contact_name")} placeholder="Jane Smith" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact email</Label>
              <Input type="email" data-testid="county-email-input" value={form.contact_email} onChange={set("contact_email")} placeholder="billing@county.gov" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Billing address</Label>
            <Input data-testid="county-address-input" value={form.address} onChange={set("address")} placeholder="123 Government Plaza" />
          </div>
          <div className="space-y-1.5">
            <Label>Annual contract amount (USD)</Label>
            <Input type="number" step="0.01" data-testid="county-amount-input" value={form.annual_contract_amount} onChange={set("annual_contract_amount")} required placeholder="24000" />
          </div>
          <div className="space-y-1.5">
            <Label>Social worker seats (blocks of 10)</Label>
            <select
              data-testid="county-seats-input"
              value={form.seat_limit}
              onChange={set("seat_limit")}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]"
            >
              {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((n) => (
                <option key={n} value={n}>{n} seats</option>
              ))}
            </select>
            <p className="text-xs text-slate-400">Each block of 10 seats grants access to 10 social workers.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Contract start</Label>
              <Input type="date" data-testid="county-start-input" value={form.contract_start} onChange={set("contract_start")} />
            </div>
            <div className="space-y-1.5">
              <Label>Contract end</Label>
              <Input type="date" data-testid="county-end-input" value={form.contract_end} onChange={set("contract_end")} />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="save-county-btn" disabled={loading} className="w-full bg-[#1e3a8a] hover:bg-[#172554]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {county ? "Save Changes" : "Add County"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
